import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "error",
    "existingUserToggle",
    "form",
    "fullNameInput",
    "passwordConfirmationInput",
    "passwordFields",
    "passwordInput",
    "submitButton",
    "tokenInput"
  ]

  static values = {
    endpoint: String,
    redirectUrl: String
  }

  connect() {
    this.tokenInputTarget.value = this.invitationToken()
    this.togglePasswordFields()

    if (!this.tokenInputTarget.value) {
      this.showError("This invitation link is missing its token. Request a new invitation and try again.")
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add("btn-disabled")
    }
  }

  toggleExistingUser() {
    this.togglePasswordFields()
  }

  async submit(event) {
    event.preventDefault()
    this.clearError()

    if (!this.tokenInputTarget.value) {
      this.showError("This invitation link is missing its token. Request a new invitation and try again.")
      return
    }

    this.setSubmitting(true)

    try {
      const response = await fetch(this.endpointValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        credentials: "same-origin",
        body: JSON.stringify({ invitation: this.payload() })
      })

      const body = await response.json().catch(() => ({}))

      if (!response.ok) {
        this.showError(this.errorMessage(body))
        return
      }

      window.location.assign(body.redirect_url || this.redirectUrlValue || "/")
    } catch (_error) {
      this.showError("We couldn't accept this invitation right now. Please try again.")
    } finally {
      this.setSubmitting(false)
    }
  }

  payload() {
    const payload = {
      invitation_token: this.tokenInputTarget.value,
      full_name: this.fullNameInputTarget.value.trim()
    }

    if (!this.existingUserToggleTarget.checked) {
      payload.password = this.passwordInputTarget.value
      payload.password_confirmation = this.passwordConfirmationInputTarget.value
    }

    return payload
  }

  togglePasswordFields() {
    const hidden = this.existingUserToggleTarget.checked

    this.passwordFieldsTarget.classList.toggle("hidden", hidden)
    this.passwordInputTarget.toggleAttribute("required", !hidden)
    this.passwordConfirmationInputTarget.toggleAttribute("required", !hidden)

    if (hidden) {
      this.passwordInputTarget.value = ""
      this.passwordConfirmationInputTarget.value = ""
    }
  }

  invitationToken() {
    return new URLSearchParams(window.location.search).get("token") || ""
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
  }

  setSubmitting(submitting) {
    this.submitButtonTarget.disabled = submitting
    this.submitButtonTarget.classList.toggle("loading", submitting)
    this.submitButtonTarget.classList.toggle("btn-disabled", submitting)
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  errorMessage(body) {
    const details = body?.error?.details

    if (details && typeof details === "object") {
      const messages = Object.values(details).flat().filter(Boolean)

      if (messages.length > 0) {
        return messages.join(" ")
      }
    }

    return body?.error?.message || "Invitation could not be accepted."
  }
}
